<template>
  <SidePanel
    :initial_width="default_width"
    storage_key="documents"
    @close="$emit('close')"
  >
    <template #header>
      <span class="documents-title">{{ title }}</span>
      <select
        v-if="languages.length > 1"
        class="documents-language"
        v-model="selected_language"
      >
        <option v-for="code in languages" :key="code" :value="code">
          {{ code.toUpperCase() }}
        </option>
      </select>
    </template>

    <div class="documents-body">
        <div class="documents-tree">
          <div v-if="loading" class="documents-message">Loading...</div>
          <div v-else-if="visible_groups.length == 0" class="documents-message">
            No documents available.
          </div>

          <div v-for="group in visible_groups" :key="group.label" class="documents-group">
            <div class="documents-group-header" @click="toggle_group(group.label)">
              <span class="documents-arrow">{{ is_expanded(group.label) ? "&#9662;" : "&#9656;" }}</span>
              {{ group.label }}
            </div>
            <div v-show="is_expanded(group.label)">
              <div
                v-for="doc in group.documents"
                :key="doc.key"
                class="documents-document"
                :class="{ selected: doc.key == selected_key }"
                @click="selected_key = doc.key"
              >
                {{ doc.label }}
                <span v-if="doc.fallback" class="documents-fallback">
                  ({{ fallback_language.toUpperCase() }})
                </span>
              </div>
            </div>
          </div>

          <!-- for a link out of the panel, such as documentation that lives elsewhere -->
          <div v-if="$slots['tree-footer'] && !loading" class="documents-footer">
            <slot name="tree-footer" />
          </div>
        </div>

        <div class="documents-viewer">
          <iframe v-if="pdf_src" :src="pdf_src" width="100%" height="100%" frameborder="0"></iframe>
          <div v-else class="documents-message">Select a document.</div>
        </div>
    </div>
  </SidePanel>
</template>

<script>
import { MANUALS_FALLBACK_LANGUAGE, MANUALS_COLLECTION } from "@/utils/manuals.js";
import SidePanel from "@/components/global/controls/SidePanel";

const cache = {
  access: null,
  languages: null,
  indexes: {},
  language: null,
  key: null,
};

export default {
  name: "DocumentsWindow",

  components: { SidePanel },

  emits: ["close"],

  props: {
    title: { type: String, required: true },
    groups: { type: Array, required: true },
  },

  data() {
    return {
      access: cache.access,
      languages: cache.languages || [MANUALS_FALLBACK_LANGUAGE],
      indexes: cache.indexes,
      selected_language: cache.language || MANUALS_FALLBACK_LANGUAGE,
      selected_key: cache.key,
      collapsed: {},
      loading: cache.access == null,
      fallback_language: MANUALS_FALLBACK_LANGUAGE,
    };
  },

  mounted() {
    this.load();
  },

  computed: {
    default_width() {
      return Math.round(window.innerWidth * 0.6);
    },

    collections() {
      const names = new Set();
      for (const group of this.groups)
        for (const doc of group.documents)
          names.add(this.collection_of(doc, group));
      return [...names];
    },

    visible_groups() {
      const groups = [];
      for (const group of this.groups) {
        const documents = [];
        for (const doc of group.documents) {
          const found = this.resolve(doc, group);
          if (found != null)
            documents.push({
              key: doc.key,
              label: this.label(doc),
              fallback: found.language != this.selected_language,
            });
        }
        if (documents.length > 0)
          groups.push({ label: group.label, documents });
      }
      return groups;
    },

    pdf_src() {
      const selected = this.entry(this.selected_key);
      if (selected == null)
        return null;

      const found = this.resolve(selected.document, selected.group);
      return found == null
        ? null
        : this.blob_url(`${found.collection}/${found.language}/${found.id}.pdf`);
    },
  },

  watch: {
    selected_key(key) {
      cache.key = key;
    },

    selected_language(code) {
      cache.language = code;
      for (const collection of this.collections)
        this.load_index(collection, code);
    },
  },

  methods: {
    load() {
      if (this.access != null) {
        this.load_languages();
        return;
      }

      this.$net.manuals.access((_app, _identification, access) => {
        cache.access = access;
        this.access = access;
        this.load_languages();
      });
    },

    collection_of(document, group) {
      return document.collection || group.collection || MANUALS_COLLECTION;
    },

    blob_url(path) {
      if (this.access == null)
        return null;
      const { storageName, container, token } = this.access;
      return `https://${storageName}.blob.core.windows.net/${container}/${path}?${token}`;
    },

    async fetch_json(path) {
      const url = this.blob_url(path);
      if (url == null)
        return null;
      try {
        const response = await fetch(url);
        if (!response.ok)
          return null;
        return await response.json();
      } catch (e) {
        console.warn(`Could not read ${path} from the documents container.`, e);
        return null;
      }
    },

    async load_languages() {
      if (cache.languages == null) {
        const available = new Set();
        for (const collection of this.collections) {
          const languages = await this.fetch_json(`${collection}/languages.json`);
          if (Array.isArray(languages))
            languages.forEach(code => available.add(code));
        }
        if (available.size > 0)
          cache.languages = [...available].sort();
      }
      if (cache.languages != null)
        this.languages = cache.languages;

      const preferred = cache.language || this.$app.session.current_language;
      const target = this.languages.includes(preferred) ? preferred : MANUALS_FALLBACK_LANGUAGE;

      for (const collection of this.collections) {
        await this.load_index(collection, target);
        if (target != MANUALS_FALLBACK_LANGUAGE)
          await this.load_index(collection, MANUALS_FALLBACK_LANGUAGE);
      }

      this.selected_language = target;
      this.loading = false;
      this.select_first();
    },

    async load_index(collection, code) {
      const cached = `${collection}/${code}`;
      if (code == null || this.access == null || this.indexes[cached] != null)
        return;

      const index = await this.fetch_json(`${collection}/${code}/index.json`);

      if (Array.isArray(index))
        this.indexes[cached] = index;
    },

    index_of(collection, language) {
      return this.indexes[`${collection}/${language}`] || null;
    },

    label(document) {
      return document.labels[this.selected_language] || document.labels[MANUALS_FALLBACK_LANGUAGE];
    },

    entry(key) {
      if (key == null)
        return null;
      for (const group of this.groups) {
        const document = group.documents.find(doc => doc.key == key);
        if (document != null)
          return { document, group };
      }
      return null;
    },

    resolve(document, group) {
      const collection = this.collection_of(document, group);

      const wanted = document.ids[this.selected_language];
      const selected = this.index_of(collection, this.selected_language);
      if (wanted != null && selected != null && selected.some(item => item.id == wanted))
        return { collection, language: this.selected_language, id: wanted };

      const spare = document.ids[MANUALS_FALLBACK_LANGUAGE];
      const fallback = this.index_of(collection, MANUALS_FALLBACK_LANGUAGE);
      if (spare != null && fallback != null && fallback.some(item => item.id == spare))
        return { collection, language: MANUALS_FALLBACK_LANGUAGE, id: spare };

      return null;
    },

    select_first() {
      if (this.selected_key != null && this.entry(this.selected_key) != null)
        return;
      const groups = this.visible_groups;
      if (groups.length > 0 && groups[0].documents.length > 0)
        this.selected_key = groups[0].documents[0].key;
    },

    is_expanded(label) {
      return this.collapsed[label] !== true;
    },

    toggle_group(label) {
      this.collapsed[label] = this.is_expanded(label);
    },
  },
};
</script>

<style scoped>
.documents-title {
  flex: 1;
  font-weight: bold;
}

.documents-language {
  padding: 2px 6px;
}

.documents-body {
  height: 100%;
  display: flex;
  min-height: 0;
}

.documents-tree {
  width: 260px;
  overflow-y: auto;
  border-right: 1px solid #e0e0e0;
  padding: 10px 0;
}

.documents-group-header {
  padding: 6px 16px;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
}

.documents-arrow {
  display: inline-block;
  width: 14px;
  color: #8b0000;
}

.documents-document {
  padding: 5px 16px 5px 30px;
  cursor: pointer;
}

.documents-document:hover {
  background-color: #f2f2f2;
}

.documents-document.selected {
  background-color: #e6eefc;
}

.documents-fallback {
  color: #888;
  font-size: 0.85em;
}

.documents-footer {
  margin-top: 10px;
  padding: 10px 16px;
  border-top: 1px solid #e0e0e0;
  font-size: 0.9em;
}

.documents-viewer {
  flex: 1;
  overflow: hidden;
}

.documents-message {
  padding: 16px;
  color: #666;
}
</style>
